import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import type { SafeString } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

export interface Args {
	model: DChartPrimaryPoint;
}

export default abstract class ForecastsDataTooltipBaseComponent<
	T extends Args,
> extends Component<T> {
	@service('event-manager') declare eventManagerService: Service.EventManager;

	@tracked top = -500;
	@tracked left = -500;

	@tracked isExplicitlyClosed = false;
	@tracked isAllPlotlinesHidden = false;

	get isVisible() {
		return !this.isExplicitlyClosed && !this.isAllPlotlinesHidden;
	}

	additionalPointMargin = 24; // distance to point, when tooltip points to it

	node!: HTMLElement;

	abstract seriesLineClass: string;
	abstract reposition(data?: unknown): void; // the subclass's own payload

	get style(): SafeString {
		return htmlSafe(`top: ${this.top}px; left: ${this.left}px;`);
	}

	get timeLabel() {
		// same as on normal chart tooltip
		return this.args.model.timeLabelLong || this.args.model.timeLabel;
	}

	getAttachTo() {
		return this.node.closest('.view-single-block')?.querySelector(`.${this.seriesLineClass}`);
	}

	private componentWidth = 0;
	getComponentWidth(): number {
		// return previous valid, if not available (e.g. was hidden before)
		const componentWidth = this.node.offsetWidth;
		if (componentWidth) {
			this.componentWidth = componentWidth;
		}
		return componentWidth || this.componentWidth;
	}

	private componentHeight = 0;
	getComponentHeight(): number {
		// return previous valid, if not available (e.g. was hidden before)
		const componentHeight = this.node.offsetHeight;
		if (componentHeight) {
			this.componentHeight = componentHeight;
		}
		return componentHeight || this.componentHeight;
	}

	getStoryContainerPosition(): DOMRect | null {
		return (
			this.node
				.closest('.main-content-box')
				?.querySelector('.story-container')
				?.getBoundingClientRect() || null
		);
	}

	getPlotLinePosition(): DOMRect | null {
		return this.getAttachTo()?.getBoundingClientRect() || null;
	}

	// when inside a popup with `transform`, `position: fixed` becomes relative
	// to the transformed ancestor instead of the viewport
	// offset viewport-relative coords by the containing block's position
	getContainingBlockOffset(): { top: number; left: number } {
		const popup = this.node.closest('.popup');
		if (popup) {
			const rect = popup.getBoundingClientRect();
			return { top: rect.top, left: rect.left };
		}
		return { top: 0, left: 0 };
	}

	@action
	handleCloseClick() {
		this.isExplicitlyClosed = true;
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;
	}
}
